import React, { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  AVAILABILITY_LABELS,
  AVAILABILITY_TYPES,
  formatDateKey,
  getAvailabilityDayClass,
  getDefaultDayAvailability,
  overridesToMap,
  mapToOverrides
} from '../../utils/workerAvailabilityUtils';

const MONTH_NAMES = [
  'Januar', 'Februar', 'Mart', 'April', 'Maj', 'Jun',
  'Jul', 'Avgust', 'Septembar', 'Oktobar', 'Novembar', 'Decembar'
];

const DAY_HEADERS = ['Pon', 'Uto', 'Sre', 'Čet', 'Pet', 'Sub', 'Ned'];

const buildMonthGrid = (year, month) => {
  const first = new Date(year, month, 1);
  const startOffset = first.getDay() === 0 ? 6 : first.getDay() - 1;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];

  for (let i = 0; i < startOffset; i += 1) {
    cells.push(null);
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(year, month, day));
  }
  while (cells.length % 7 !== 0) {
    cells.push(null);
  }
  return cells;
};

const WorkerAvailabilityCalendar = ({ overrides = [], onChange }) => {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(formatDateKey(today));
  const [dropdownType, setDropdownType] = useState(AVAILABILITY_TYPES.WORK);

  const overrideMap = useMemo(() => overridesToMap(overrides), [overrides]);

  const monthCells = useMemo(
    () => buildMonthGrid(viewYear, viewMonth),
    [viewYear, viewMonth]
  );

  const shiftMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const applyDayType = (dateKey, nextType) => {
    const nextMap = new Map(overrideMap);
    const defaultType = getDefaultDayAvailability(dateKey);
    if (!nextType || nextType === defaultType) {
      nextMap.delete(dateKey);
    } else {
      nextMap.set(dateKey, nextType);
    }
    onChange(mapToOverrides(nextMap));
  };

  const handleDayClick = (date) => {
    if (!date) return;
    const dateKey = formatDateKey(date);
    setSelectedDate(dateKey);
    const defaultType = getDefaultDayAvailability(dateKey);
    applyDayType(dateKey, dropdownType === defaultType ? null : dropdownType);
  };

  const handleTypeSelect = (e) => {
    setDropdownType(e.target.value);
  };

  return (
    <div className="rounded-lg border border-gray-600 bg-gray-700/40 p-3 sm:p-4">
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => shiftMonth(-1)}
          className="p-1.5 rounded-md text-gray-300 hover:bg-gray-600 transition-colors"
          aria-label="Prethodni mesec"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <p className="text-sm font-semibold text-white">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </p>
        <button
          type="button"
          onClick={() => shiftMonth(1)}
          className="p-1.5 rounded-md text-gray-300 hover:bg-gray-600 transition-colors"
          aria-label="Sledeći mesec"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAY_HEADERS.map((label) => (
          <div key={label} className="text-center text-[10px] text-gray-400 font-medium py-1">
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {monthCells.map((date, index) => {
          if (!date) {
            return <div key={`empty-${index}`} className="aspect-square" />;
          }
          const dateKey = formatDateKey(date);
          const type = overrideMap.get(dateKey) || getDefaultDayAvailability(dateKey);
          const isSelected = dateKey === selectedDate;
          const isToday = dateKey === formatDateKey(today);

          return (
            <button
              key={dateKey}
              type="button"
              onClick={() => handleDayClick(date)}
              className={`aspect-square rounded-md text-xs font-semibold transition-all ${getAvailabilityDayClass(type)} ${
                isSelected ? 'ring-2 ring-white ring-offset-1 ring-offset-gray-800' : ''
              } ${isToday && !isSelected ? 'outline outline-1 outline-white/50' : ''}`}
              title={AVAILABILITY_LABELS[type]}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <label className="text-xs text-gray-400 whitespace-nowrap">
          Tip za označavanje:
        </label>
        <select
          value={dropdownType}
          onChange={handleTypeSelect}
          className="flex-1 max-w-[10rem] h-8 rounded-md border border-gray-600 bg-gray-700/90 text-white text-xs px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-500/40 focus:border-blue-500"
        >
          {Object.entries(AVAILABILITY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <span className="text-[10px] text-gray-500">
          Izabrani dan: {selectedDate}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-gray-400">
        {Object.entries(AVAILABILITY_LABELS).map(([type, label]) => (
          <span key={type} className="inline-flex items-center gap-1">
            <span className={`w-3 h-3 rounded-sm ${getAvailabilityDayClass(type)}`} />
            {label}
          </span>
        ))}
        <span className="text-gray-500 w-full sm:w-auto">
          Izaberite tip u padajućem meniju, zatim kliknite dane u kalendaru. Podrazumevano: pon–sub radni, nedelja slobodna.
        </span>
      </div>
    </div>
  );
};

export default WorkerAvailabilityCalendar;
